/* TimeTuck theme behaviour: product gallery, bundle selector, sticky add to cart. */

(() => {
  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP',
      }).format(cents / 100);
    } catch (e) {
      return `£${(cents / 100).toFixed(2)}`;
    }
  };

  /* ---------- Gallery ---------- */
  if (!customElements.get('tt-gallery')) {
    customElements.define(
      'tt-gallery',
      class TTGallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-gallery-track]');
          if (!this.track) return;
          this.slides = Array.from(this.track.children);
          this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
          this.prev = this.querySelector('[data-gallery-prev]');
          this.next = this.querySelector('[data-gallery-next]');
          this.counter = this.querySelector('[data-gallery-counter]');
          this.index = 0;

          if (this.slides.length < 2) {
            [this.prev, this.next, this.counter].forEach((el) => el && el.setAttribute('hidden', ''));
            const thumbs = this.querySelector('.tt-gallery__thumbs');
            if (thumbs) thumbs.setAttribute('hidden', '');
            return;
          }

          this.thumbs.forEach((thumb) =>
            thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.galleryThumb)))
          );
          this.prev && this.prev.addEventListener('click', () => this.goTo(this.index - 1));
          this.next && this.next.addEventListener('click', () => this.goTo(this.index + 1));
          this.track.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight') { e.preventDefault(); this.goTo(this.index + 1); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); this.goTo(this.index - 1); }
          });

          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                  this.setActive(this.slides.indexOf(entry.target));
                }
              });
            },
            { root: this.track, threshold: [0.6] }
          );
          this.slides.forEach((slide) => this.observer.observe(slide));
          this.update();
        }

        disconnectedCallback() {
          this.observer && this.observer.disconnect();
        }

        goTo(i) {
          const target = Math.max(0, Math.min(this.slides.length - 1, i));
          const slide = this.slides[target];
          if (!slide) return;
          this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
          this.setActive(target);
        }

        goToMedia(mediaId) {
          const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
          if (i > -1) this.goTo(i);
        }

        setActive(i) {
          if (i < 0 || i === this.index) return;
          this.index = i;
          this.update();
          // Pause any video that is no longer visible.
          this.slides.forEach((slide, idx) => {
            if (idx === i) return;
            slide.querySelectorAll('video').forEach((v) => v.pause());
            slide.querySelectorAll('iframe').forEach((f) => {
              try {
                f.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
                f.contentWindow.postMessage('{"method":"pause"}', '*');
              } catch (e) { /* cross-origin */ }
            });
          });
        }

        update() {
          this.thumbs.forEach((thumb, idx) => thumb.setAttribute('aria-current', idx === this.index ? 'true' : 'false'));
          const active = this.thumbs[this.index];
          if (active) {
            const list = active.closest('ul');
            const li = active.parentElement;
            if (list && li) {
              const left = li.offsetLeft - list.clientWidth / 2 + li.clientWidth / 2;
              list.scrollTo({ left, behavior: 'smooth' });
            }
          }
          if (this.prev) this.prev.disabled = this.index === 0;
          if (this.next) this.next.disabled = this.index === this.slides.length - 1;
          if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
        }
      }
    );
  }

  /* ---------- Product (bundles + variants) ---------- */
  if (!customElements.get('tt-product')) {
    customElements.define(
      'tt-product',
      class TTProduct extends HTMLElement {
        connectedCallback() {
          const json = this.querySelector('[data-product-json]');
          this.data = json ? JSON.parse(json.textContent) : { variants: [] };
          this.form = document.getElementById(this.dataset.formId);
          this.button = this.querySelector(`#ProductSubmitButton-${this.dataset.sectionId}`);
          this.label = this.querySelector('[data-atc-label]');
          this.atcPrice = this.querySelector('[data-atc-price]');
          this.price = this.querySelector('[data-price]');
          this.compare = this.querySelector('[data-compare-price]');
          this.bundleLine = this.querySelector('[data-price-bundle]');
          this.stickyPrice = this.querySelector('[data-sticky-price]');
          this.stickyButton = this.querySelector('[data-sticky-button]');
          this.radios = Array.from(this.querySelectorAll('.tt-bundle__input'));
          this.variantSelect = this.querySelector('[data-variant-select]');

          const idInput = this.form && this.form.querySelector('[name="id"]');
          const currentId = idInput ? Number(idInput.value) : null;
          this.variant = this.data.variants.find((v) => v.id === currentId) || this.data.variants[0];

          this.radios.forEach((r) => r.addEventListener('change', () => this.render()));
          if (this.variantSelect) {
            this.variantSelect.addEventListener('change', () => {
              this.variant = this.data.variants.find((v) => v.id === Number(this.variantSelect.value));
              this.onVariantChange();
            });
          }
          this.render();
        }

        money(cents) {
          return formatMoney(cents, this.data.currency);
        }

        get selected() {
          const checked = this.radios.find((r) => r.checked);
          return {
            qty: checked ? Number(checked.value) : 1,
            savings: checked ? Number(checked.dataset.savings) || 0 : 0,
          };
        }

        tierTotal(qty, savings) {
          return Math.max(0, this.variant.price * qty - savings);
        }

        onVariantChange() {
          const v = this.variant;
          if (!v) return;
          const idInput = this.form && this.form.querySelector('[name="id"]');
          if (idInput) {
            idInput.value = v.id;
            idInput.disabled = !v.available;
          }

          // Recalculate bundle cards and disable tiers that exceed real tracked stock.
          this.radios.forEach((radio) => {
            const qty = Number(radio.dataset.qty);
            const savings = Number(radio.dataset.savings) || 0;
            const card = radio.nextElementSibling;
            const set = (sel, text) => {
              const el = card.querySelector(sel);
              if (el) el.textContent = text;
            };
            set('[data-tier-price]', this.money(this.tierTotal(qty, savings)));
            set('[data-tier-compare]', this.money(v.price * qty));
            set('[data-tier-each]', `${this.money(v.price)} each`);
            radio.disabled = v.tracked && v.deny && qty > v.inventory;
          });
          if (this.radios.length && !this.radios.some((r) => r.checked && !r.disabled)) {
            const first = this.radios.find((r) => !r.disabled);
            if (first) first.checked = true;
          }
          this.querySelectorAll('[data-unit-price]').forEach((el) => (el.textContent = this.money(v.price)));

          // Low-stock line uses Shopify's live quantity only.
          const stock = this.querySelector('[data-stock]');
          if (stock) {
            const threshold = Number(stock.dataset.threshold);
            const show = v.tracked && v.available && v.inventory > 0 && v.inventory <= threshold;
            stock.hidden = !show;
            if (show) stock.querySelector('[data-stock-text]').textContent = stock.dataset.template.replace('[count]', v.inventory);
          }

          if (v.image) {
            const gallery = this.querySelector('tt-gallery');
            gallery && gallery.goToMedia && gallery.goToMedia(v.image);
          }

          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState({}, '', url.toString());
          this.render();
        }

        render() {
          const v = this.variant;
          if (!v) return;
          const { qty, savings } = this.selected;
          const total = this.tierTotal(qty, savings);
          const totalText = this.money(total);

          // Headline price follows the selected bundle.
          if (this.price) this.price.textContent = totalText;

          // Product compare-at price: single unit only, and only when enabled + configured.
          if (this.compare) {
            const show = qty === 1 && this.data.showCompare && v.compare_at_price > v.price;
            this.compare.hidden = !show;
            if (show) this.compare.lastChild.textContent = this.money(v.compare_at_price);
          }

          // Bundle line: "2 × £44.99" with the individual-price equivalent crossed out.
          if (this.bundleLine) {
            const show = qty > 1 && savings > 0;
            this.bundleLine.hidden = !show;
            if (show) {
              this.bundleLine.querySelector('[data-price-bundle-qty]').textContent = `${qty} × ${this.money(v.price)}`;
              this.bundleLine.querySelector('[data-price-bundle-full]').textContent = this.money(v.price * qty);
              this.bundleLine.querySelector('[data-price-bundle-save]').textContent = `Save ${this.money(savings)}`;
            }
          }

          if (this.button) this.button.disabled = !v.available;
          if (this.stickyButton) this.stickyButton.disabled = !v.available;
          if (this.label) this.label.textContent = v.available ? this.data.addLabel || 'Add to cart' : this.data.soldOutLabel;
          if (this.atcPrice) {
            this.atcPrice.hidden = !v.available;
            this.atcPrice.textContent = totalText;
          }
          if (this.stickyPrice) {
            this.stickyPrice.textContent = qty > 1 ? `${qty} orbs · ${totalText}` : totalText;
          }
        }
      }
    );
  }

  /* ---------- Sticky add to cart ---------- */
  if (!customElements.get('tt-sticky-atc')) {
    customElements.define(
      'tt-sticky-atc',
      class TTStickyATC extends HTMLElement {
        connectedCallback() {
          this.target = document.getElementById(this.dataset.target);
          this.button = this.querySelector('button');
          if (!this.target) return;
          this.footer = document.querySelector('.footer');
          this.visible = false;
          this.ticking = false;

          // Position check on scroll (rAF-throttled): reliable even when a fast fling or
          // anchor jump skips past the button without it ever intersecting the viewport.
          this.onScroll = () => {
            if (this.ticking) return;
            this.ticking = true;
            requestAnimationFrame(() => {
              this.ticking = false;
              this.update();
            });
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
          this.update();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
          document.body.classList.remove('tt-sticky-open');
        }

        update() {
          // Only show once the user has scrolled *past* the main button, and hide again at the footer.
          const pastButton = this.target.getBoundingClientRect().bottom < 0;
          const footerInView = this.footer ? this.footer.getBoundingClientRect().top < window.innerHeight : false;
          const show = pastButton && !footerInView;
          if (show !== this.visible) this.toggle(show);
        }

        toggle(show) {
          this.visible = show;
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', show ? 'false' : 'true');
          if (this.button) this.button.tabIndex = show ? 0 : -1;
          document.body.classList.toggle('tt-sticky-open', show);
        }
      }
    );
  }
})();
